import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Skeleton, Typography } from "@mui/material";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTerminology } from "../../Core/useTerminology";
import { useRecentItems } from "../../Query/useDashboard";
import DashboardCard, { EmptyState } from "./DashboardCard";
import {
  ItemIcon,
  StatusPill,
  itemPath,
  itemStatus,
  makeTimeAgo,
} from "./dashboardItems";
import { useDashboardColors } from "./dashboardColors";

const RecentItemsCard: React.FC = () => {
  const { t, i18n } = useTranslation("home");
  const navigate = useNavigate();
  const terminology = useTerminology();
  const { colors: workspace } = useWorkspaceColors();
  const { accentSoft } = useDashboardColors();
  const { data: items = [], isLoading } = useRecentItems(6);
  const timeAgo = makeTimeAgo(t, i18n.language);

  return (
    <DashboardCard
      title={t("hub.recent.title")}
      subtitle={t("hub.recent.subtitle")}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[0, 1, 2].map((key) => (
            <Box
              key={key}
              sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
            >
              <Skeleton variant="rounded" width={36} height={36} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="70%" />
                <Skeleton width="40%" />
              </Box>
            </Box>
          ))}
        </Box>
      ) : items.length === 0 ? (
        <EmptyState>{t("hub.recent.empty")}</EmptyState>
      ) : (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mx: -1 }}>
          {items.map((item) => {
            const status = itemStatus(item, t);
            const meta = [
              t(`hub.search.types.${item.type}`, {
                entity: terminology.singular,
              }),
              timeAgo(item.timestamp),
            ]
              .filter(Boolean)
              .join(" · ");

            return (
              <Box component="li" key={`${item.type}-${item.id}`}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => navigate(itemPath(item))}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1,
                    py: 1,
                    border: 0,
                    borderRadius: "10px",
                    background: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontFamily: "inherit",
                    "&:hover": {
                      backgroundColor: accentSoft,
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${workspace.primary_dark}`,
                      outlineOffset: -2,
                    },
                  }}
                >
                  <ItemIcon item={item} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14,
                        lineHeight: "20px",
                        fontWeight: 600,
                        color: color.gray900,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 12,
                        lineHeight: "16px",
                        color: color.gray500,
                      }}
                    >
                      {meta}
                    </Typography>
                  </Box>
                  {status && (
                    <StatusPill label={status.label} tone={status.tone} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </DashboardCard>
  );
};

export default RecentItemsCard;
